import { apiClient } from './client';

export interface Corral {
  corralId:  string;
  tenantId:  string;
  ranchoId:  string;
  name:      string;
  capacity:  number;
  status:    'EMPTY' | 'IN_USE' | 'ACTIVE' | 'INACTIVE';
  notes?:    string;
  createdAt: string;
}

export interface LotePorcino {
  loteId:               string;
  corralId:             string;
  tenantId:             string;
  ranchoId:             string;
  cabezasIniciales:     number;
  pesoPromedioInicial:  number;
  costoPorCabeza?:      number;
  costoCompraTotal?:    number;
  proveedor?:           string;
  cabezasActuales:      number;
  pesoPromedioActual:   number;
  kgAlimentoTotal:      number;
  bajas:                number;
  status:               'ACTIVE' | 'SOLD' | 'CLOSED';
  entradaAt:            string;
  ventaAt?:             string;
  totalVenta?:          number;
}

export interface PorcinoKPIs {
  diasEnCorral:         number;
  cabezasIniciales:     number;
  cabezasActuales:      number;
  pesoPromedioActual:   number;
  gdp:                  number | null;
  conversionAlimenticia: number | null;
  mortalidadPct:        number;
  costoKgEnPie:         number | null;
}

export interface EventoPorcino {
  eventoId:     string;
  corralId:     string;
  tenantId:     string;
  tipo:         string;
  fecha:        string;
  registradoPor: string;
  /** Nombre resuelto en servidor a partir de registradoPor — null si no se pudo resolver */
  registradoPorNombre?: string | null;
  cabezas?:     number;
  pesoPromedio?: number;
  kgAlimento?:  number;
  causa?:       string;
  totalCost?:   number;
  // Campos tal como los guarda el backend (EventoPorcino en porcino.model.ts).
  cabezasEntrada?:      number;
  pesoPromedioEntrada?: number;
  pesoPromedioNuevo?:   number;
  tipoAlimento?:        string;
  cabezasMuertas?:      number;
  causaMortalidad?:     string;
  cabezasVendidas?:     number;
  precioKg?:            number;
  totalVenta?:          number;
  destino?:             string;
  notas?:               string;
  medicamento?:         string;
  dosis?:               string;
}

export const fetchCorrales = async (ranchoId?: string): Promise<Corral[]> =>
  (await apiClient.get('/api/porcino/corrales', { params: ranchoId ? { ranchoId } : {} })).data;

export const fetchCorral = async (corralId: string): Promise<Corral> =>
  (await apiClient.get(`/api/porcino/corrales/${corralId}`)).data;

export const fetchLoteActivo = async (
  corralId: string,
): Promise<{ lote: LotePorcino; kpis: PorcinoKPIs } | null> =>
  (await apiClient.get(`/api/porcino/corrales/${corralId}/lote-activo`)).data;

export const fetchEventos = async (corralId: string, limit = 50): Promise<EventoPorcino[]> =>
  (await apiClient.get(`/api/porcino/corrales/${corralId}/eventos`, { params: { limit } })).data;

// ── Mutaciones (dashboard) ────────────────────────────────────────────────────

export interface CreateCorralInput {
  ranchoId: string;
  name:     string;
  capacity: number;
  notes?:   string;
}

export const createCorral = async (input: CreateCorralInput): Promise<Corral> =>
  (await apiClient.post('/api/porcino/corrales', input)).data;

export interface RegistrarEntradaInput {
  ranchoId:        string;
  cabezas:         number;
  pesoPromedio:    number;
  costoPorCabeza?: number;
  proveedor?:      string;
  notas?:          string;
}

export const registrarEntrada = async (corralId: string, input: RegistrarEntradaInput): Promise<LotePorcino> =>
  (await apiClient.post(`/api/porcino/corrales/${corralId}/entrada`, input)).data;

export const registrarPesaje = async (
  corralId: string, input: { loteId: string; pesoPromedio: number; notas?: string },
): Promise<EventoPorcino> =>
  (await apiClient.post(`/api/porcino/corrales/${corralId}/pesaje`, input)).data;

export const registrarAlimento = async (
  corralId: string, input: { loteId: string; kgAlimento: number; tipoAlimento?: string; notas?: string },
): Promise<EventoPorcino> =>
  (await apiClient.post(`/api/porcino/corrales/${corralId}/alimento`, input)).data;

export const registrarMortalidad = async (
  corralId: string, input: { loteId: string; cabezas: number; causa?: string; notas?: string },
): Promise<EventoPorcino> =>
  (await apiClient.post(`/api/porcino/corrales/${corralId}/mortalidad`, input)).data;

export const registrarVenta = async (
  corralId: string, input: { loteId: string; cabezas: number; pesoTotal: number; precioKg: number; destino?: string; notas?: string },
): Promise<EventoPorcino> =>
  (await apiClient.post(`/api/porcino/corrales/${corralId}/venta`, input)).data;
